import { categoryFetcher } from '@/data/Content/_CategoryFetcher';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { getContractIdParamFromContext, useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { TOP_CATEGORIES_DEPTH_LIMIT } from '@/data/config/TOP_CATEGORIES_DEPTH_LIMIT';
import { DATA_KEY_NAVIGATION } from '@/data/constants/dataKey';
import { CategoryType } from '@/data/types/Category';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import { useMemo } from 'react';
import useSWR from 'swr';
import type { IconName } from 'presentation/components/custom/icons/DynamicIcon';

export type Categories = {
	uniqueID: string;
	label: string;
	url?: string;
	children: Categories[];
	trail?: string[];
	thumbnail?: string | IconName;
	identifier?: string;
	id: string;
};

const dataCategoriesMap = (contents: CategoryType[] = [], trail: string[] = []): Categories[] =>
	contents.map(
		({
			name,
			seo,
			children,
			uniqueID,
			thumbnail,
			identifier,
			id,
		}: CategoryType & { id?: string }) => {
			const result: Categories = {
				uniqueID,
				label: name,
				url: seo?.href || '',
				children: dataCategoriesMap(children, [...trail, uniqueID, name, seo?.href || '']),
				trail,
				...(thumbnail && { thumbnail }),
				...(identifier && { identifier }),
				id: id || '',
			};
			return result;
		}
	);

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (props: any, params: RequestParams): Promise<CategoryType[]> =>
		(await categoryFetcher(pub, context)(props, params)) ?? [];

export const useGetCategories = () => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const { AllCategoriesLabel } = useLocalization('AllCategoriesExpandedMenu');
	const allCatLabelText = AllCategoriesLabel.t();
	const { user } = useUser();
	const params = useExtraRequestParameters();
	const { data, error } = useSWR(
		storeId
			? [
					shrink({
						storeId,
						depthAndLimit: TOP_CATEGORIES_DEPTH_LIMIT,
						...getContractIdParamFromContext(user?.context),
						langId,
					}),
					DATA_KEY_NAVIGATION,
			  ]
			: null,
		// cache the RAW category response under the shared key so `useNavigation`
		// reuses the same entry; the `Categories` projection happens below
		async ([props]) => fetcher(true)(expand(props), params),
		// the SSR `getNavigation` seeds this key's fallback from the same category
		// fetch, so when a fallback exists we skip the redundant client request
		{ use: [laggyMiddleWare], revalidateIfStale: false }
	);

	const navigation = useMemo(() => {
		if (!data) return [];

		const categories = dataCategoriesMap(data);

		// Sort by label
		const sortedData = [...categories].sort((a, b) => {
			// 1. Force identifier '600' to the top
			if (String(a.identifier) === '600') return -1;
			if (String(b.identifier) === '600') return 1;

			// 2. Otherwise, sort alphabetically by label
			return a.label.localeCompare(b.label);
		});

		return [{ label: allCatLabelText, children: categories }, ...sortedData] as Categories[];
	}, [allCatLabelText, data]);

	return {
		navigation,
		loading: !error && !data,
		error,
	};
};
